import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getCodeChange, removeCodeChange } from "../lib/adminPortal";
import { ChevronRight } from "lucide-react";
import { relativeDate } from "../lib/ownerUi";
import BackBar from "../components/BackBar";

export default function AdminCodeChangeDetail() {
  const { ccId } = useParams();
  const { user } = useRole();
  const navigate = useNavigate();
  const [cc, setCc] = useState(null);
  const [err, setErr] = useState(null);
  const [showRemove, setShowRemove] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    getCodeChange(ccId, user.id).then(setCc).catch(setErr);
  }, [user?.id, ccId]);

  if (err) return <p style={{ color: "#A23B2E" }} data-testid="admin-cc-detail-error">Could not load this entry.</p>;
  if (!cc) return <div style={{ color: "#6C6B85", fontStyle: "italic" }}>Loading…</div>;

  const onRemove = async () => {
    setBusy(true);
    try {
      await removeCodeChange(ccId, user.id, reason.trim());
      navigate("/admin/code-changes");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="animate-fade-in-up" data-testid="admin-cc-detail">
      <BackBar
        parent={{ to: "/admin/code-changes", label: "Code Changes" }}
        trail={[
          { label: "Admin", to: "/admin" },
          { label: "Code Changes", to: "/admin/code-changes" },
          { label: cc.id },
        ]}
      />

      <h1
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: "clamp(22px, 3vw, 28px)",
          fontWeight: 600,
          color: "#1B1F4B",
          maxWidth: 820,
          lineHeight: 1.2,
        }}
        data-testid="admin-cc-summary"
      >
        {cc.summary}
      </h1>
      <div
        className="tabular-nums"
        style={{ color: "#3A5563", fontWeight: 600, fontSize: 13, marginTop: 6 }}
      >
        Effective {cc.effective_date}
      </div>
      <div
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 13,
        }}
      >
        Recorded by {cc.recorded_by_user?.name || cc.recorded_by} · {relativeDate(cc.recorded_at)}
      </div>

      {cc.removed_at && (
        <div
          className="wn-card mt-5"
          style={{ padding: 14, background: "rgba(162,59,46,0.06)", borderColor: "#A23B2E" }}
          data-testid="admin-cc-removed-banner"
        >
          <div style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, color: "#A23B2E", fontSize: 14 }}>
            This entry was removed from the register.
          </div>
          <div style={{ color: "#272636", fontSize: 12.5, marginTop: 4, whiteSpace: "pre-wrap" }}>
            {cc.removal_reason}
          </div>
        </div>
      )}

      <div className="mt-7 grid lg:grid-cols-[3fr_2fr] gap-7">
        <div className="space-y-6">
          {cc.notes && (
            <div className="wn-card" style={{ padding: 18 }}>
              <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
                Notes.
              </h2>
              <p style={{ color: "#272636", fontSize: 14, marginTop: 8, whiteSpace: "pre-wrap" }}>
                {cc.notes}
              </p>
            </div>
          )}

          <div className="wn-card" style={{ padding: 18 }} data-testid="admin-cc-affected">
            <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
              Affected designs ({(cc.affected_designs || []).length}).
            </h2>
            {(cc.affected_designs || []).length === 0 && (
              <p style={{ color: "#6C6B85", fontSize: 13, marginTop: 6 }}>
                No specific designs were flagged. The change may apply broadly across the catalog.
              </p>
            )}
            <div className="mt-3 space-y-2">
              {(cc.affected_designs || []).map((d) => (
                <Link
                  key={d.id}
                  to={`/marketplace/${d.id}`}
                  className="flex items-center justify-between gap-3 wn-card"
                  style={{ padding: 10, textDecoration: "none" }}
                >
                  <div className="min-w-0">
                    <div
                      style={{
                        fontFamily: '"Fraunces", Georgia, serif',
                        fontWeight: 600,
                        fontSize: 13.5,
                        color: "#1B1F4B",
                      }}
                    >
                      {d.name}
                    </div>
                    <div className="tabular-nums" style={{ color: "#6C6B85", fontSize: 11 }}>
                      {d.id} · {d.status}
                    </div>
                  </div>
                  <ChevronRight size={14} style={{ color: "#9AA0C0" }} />
                </Link>
              ))}
            </div>
          </div>
        </div>

        <aside data-testid="admin-cc-actions">
          {!cc.removed_at && (
            <div className="wn-card" style={{ padding: 18 }}>
              <h2 style={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: 18 }}>
                Register actions.
              </h2>
              {!showRemove ? (
                <>
                  <p style={{ color: "#6C6B85", fontSize: 12.5, marginTop: 4 }}>
                    Removal is for correction purposes only. The action is audit-logged.
                  </p>
                  <button
                    className="wn-btn wn-btn-tertiary mt-3 w-full"
                    style={{ color: "#A23B2E", justifyContent: "center" }}
                    onClick={() => setShowRemove(true)}
                    data-testid="admin-cc-remove-open"
                  >
                    Remove from register
                  </button>
                </>
              ) : (
                <div className="mt-3" data-testid="admin-cc-remove-form">
                  <label className="wn-label block">Reason (≥ 20 chars)</label>
                  <textarea
                    className="wn-input"
                    style={{ minHeight: 90, resize: "vertical" }}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="admin-cc-remove-reason"
                  />
                  <div
                    className="tabular-nums"
                    style={{ fontSize: 11, color: reason.trim().length < 20 ? "#A23B2E" : "#6C6B85", marginTop: 2 }}
                  >
                    {reason.trim().length} / 20 characters
                  </div>
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      className="wn-btn wn-btn-tertiary"
                      onClick={() => { setShowRemove(false); setReason(""); }}
                    >
                      Cancel
                    </button>
                    <button
                      className="wn-btn"
                      style={{ background: "#A23B2E", color: "#FBFAFD" }}
                      onClick={onRemove}
                      disabled={busy || reason.trim().length < 20}
                      data-testid="admin-cc-remove-confirm"
                    >
                      {busy ? "Working…" : "Confirm removal"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
